import React, { useState } from "react";
import { motion } from "framer-motion";
import { Quote, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const RISE = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

const URL_SPLIT = /(https?:\/\/[^\s)]+)/g;
// Matches **bold** or *italic* (bold checked first so it wins).
const INLINE = /(\*\*([^*]+)\*\*|\*([^*\n]+)\*)/g;

// Render **bold** and *italics* inside a plain-text segment.
function renderInline(text, prefix) {
  const nodes = [];
  let last = 0;
  let m;
  let i = 0;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) {
      nodes.push(<strong key={`${prefix}-b-${i}`} className="font-medium">{m[2]}</strong>);
    } else {
      nodes.push(<em key={`${prefix}-i-${i}`}>{m[3]}</em>);
    }
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// Render text with links AND markdown emphasis, never leaking raw asterisks.
function renderRich(text) {
  if (!text) return text;
  return text.split(URL_SPLIT).map((part, idx) => {
    if (/^https?:\/\//.test(part)) {
      const trail = (part.match(/[.,;:!?]+$/) || [""])[0];
      const href = trail ? part.slice(0, -trail.length) : part;
      return (
        <React.Fragment key={`u-${idx}`}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-dotted underline-offset-2 text-[var(--mirror-accent)] hover:opacity-80 break-words"
          >
            {href}
          </a>
          {trail}
        </React.Fragment>
      );
    }
    return <React.Fragment key={`t-${idx}`}>{renderInline(part, `s${idx}`)}</React.Fragment>;
  });
}

const CopyButton = ({ text, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      toast("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast("Could not copy");
    }
  };
  return (
    <button
      data-testid={testid}
      onClick={copy}
      aria-label="Copy message"
      className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-300 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] shrink-0"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

export const ThinkingDots = () => (
  <div className="flex gap-2 py-2" data-testid="mirror-thinking">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="thinking-dot h-1.5 w-1.5 rounded-full bg-[var(--mirror-text-2)]"
        style={{ animationDelay: `${i * 0.3}s` }}
      />
    ))}
  </div>
);

export const ChatMessage = ({ m }) => {
  const isUser = m.role === "user";
  return (
    <motion.div
      initial={RISE.initial}
      animate={RISE.animate}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={`group flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`message-${m.role}`}
    >
      <div className={isUser ? "max-w-[80%]" : "max-w-full"}>
        {isUser ? (
          <div className="flex items-end gap-2 justify-end">
            <CopyButton text={m.content} testid={`copy-message-${m.id || "user"}`} />
            <div className="rounded-2xl rounded-br-sm bg-[var(--mirror-surface)] px-5 py-3 text-[var(--mirror-text)] text-base leading-relaxed whitespace-pre-wrap">
              {renderRich(m.content)}
            </div>
          </div>
        ) : (
          <div>
            <p className="text-[var(--mirror-text)] text-base sm:text-lg leading-relaxed whitespace-pre-wrap font-light">
              {renderRich(m.content)}
            </p>
            <div className="mt-2">
              <CopyButton text={m.content} testid={`copy-message-${m.id || "assistant"}`} />
            </div>
            {Array.isArray(m.citations) && m.citations.length > 0 ? (
              <div data-testid="message-citation" className="mt-4 space-y-2">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[var(--mirror-text-3)]">
                  Sources
                </p>
                {m.citations.map((c, i) => (
                  <a
                    key={c.url || i}
                    data-testid={`citation-link-${i}`}
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 rounded-lg border border-[var(--mirror-border)] px-4 py-3 text-sm text-[var(--mirror-text-2)] hover:border-[var(--mirror-border-strong)] hover:text-[var(--mirror-text)] transition-colors duration-500"
                  >
                    <Quote size={14} className="mt-1 shrink-0 text-[var(--mirror-accent)]" />
                    <span className="break-words">{c.title || c.url}</span>
                  </a>
                ))}
              </div>
            ) : m.no_sources ? (
              <p
                data-testid="message-no-sources"
                className="mt-3 text-[11px] italic text-[var(--mirror-text-3)] opacity-70"
              >
                Reflecting without sources.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </motion.div>
  );
};
